import { useState } from 'react';
import { useRouter } from 'next/router';

import { Anchor, Box, Card, Paragraph, TextButton } from '@zillow/constellation-10';

import { LoadingText } from '@zg-rentals/loading-components';
import {
  ListingScoreConstants,
  ScoreDetailsModal,
  ScorePieChart,
  ZRAMListingScoreConstants,
} from '@zg-rentals/rental-revenue-components';

import { useGetListingDetails, useGetListingRecommendations } from '../../hooks/queries/properties';
import { useScreenSize } from '../../hooks/useScreenSize';
import stringHelper from '../../utils/stringHelper';

import Heading from './Heading';

import type { ReactNode } from 'react';
import type { CompletenessScoreType } from '@zg-rentals/rental-revenue-components';
import type { RentalManagerListingRecommendationDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  isFromPremiumAnalyticsPage?: boolean;
  isSkeleton?: boolean;
  isFromDashboard?: boolean;
  canResumeModularListingCreation?: boolean;
  requiredFieldsErrors?: Array<boolean>;
  previewListingCTA?: ReactNode;
};

const ListingCompletenessScore = ({
  isFromPremiumAnalyticsPage = false,
  isSkeleton = false,
  isFromDashboard = false,
  canResumeModularListingCreation = false,
  requiredFieldsErrors = [],
  previewListingCTA,
}: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { isScreenMobile, isScreenTablet, isScreenDesktop } = useScreenSize();

  const showIncompleteMessage = canResumeModularListingCreation || requiredFieldsErrors?.length > 0;
  const [displayScoreDetailsModal, setDisplayScoreDetailsModal] = useState(false);

  const { data: propertyDetailsData, isPending: isListingDetailsLoading } = useGetListingDetails(listingAlias, {
    enabled: !isSkeleton,
  });

  const {
    recommendationsData,
    error: listingRecsError,
    isFetching: isFetchingListingRecommendations,
  } = useGetListingRecommendations(
    { listingAlias, isMultifamily: propertyDetailsData?.isMultifamily },
    { enabled: !!listingAlias && !isSkeleton },
  );

  const isLoading = isListingDetailsLoading || isFetchingListingRecommendations;
  const isMultifamily = propertyDetailsData?.isMultifamily;
  const isFeed = propertyDetailsData?.isFeed;

  if (
    !isFetchingListingRecommendations &&
    (listingRecsError ||
      !recommendationsData ||
      !(
        recommendationsData?.completeness?.rating in ListingScoreConstants ||
        recommendationsData?.completeness?.rating in ZRAMListingScoreConstants
      ))
  ) {
    return null;
  }

  const { completeness, recommendations } = recommendationsData || {};

  const completenessScore: CompletenessScoreType = showIncompleteMessage
    ? { score: 20, rating: 'incomplete' }
    : (completeness as CompletenessScoreType);

  const totalIncompleteRecommendations =
    recommendations?.filter((item: RentalManagerListingRecommendationDto) => !item.isComplete).length || 0;

  const getSubText = () => {
    if (isMultifamily) {
      if (showIncompleteMessage) {
        return 'Your listing won’t be published until required details are added. Complete the required fields to publish your listing.';
      }
      if (totalIncompleteRecommendations === 0) {
        return 'Your listing quality is outstanding and has all the details renters need!';
      }
      if (completeness?.leadImpact) {
        return `Adding missing details may help increase your leads by up to ${completeness.leadImpact}% and improve visibility in filtered renter searches.`;
      }
      return 'Add missing details to improve completeness to help prevent negative impacts to your leads and keep your listings visible in more filtered renter searches.';
    }

    if (showIncompleteMessage) {
      return `Complete ${stringHelper.pluralize(
        requiredFieldsErrors.length,
        'key section',
        'key sections',
      )} to publish your listing. `;
    } else if (totalIncompleteRecommendations === 0) {
      return isFromPremiumAnalyticsPage
        ? `Renters should have plenty of information to help decide if they’re right for your rental. `
        : 'All listing recommendations complete. Great work! ';
    } else if (isFromPremiumAnalyticsPage) {
      const listingScoreDetails =
        ListingScoreConstants[completenessScore?.rating as keyof typeof ListingScoreConstants];
      return `${listingScoreDetails?.premiumAnalyticsSubText || ''} ${stringHelper.pluralizeWord(
        totalIncompleteRecommendations,
        'recommendation',
        'recommendations',
      )}. `;
    }

    return `Follow ${stringHelper.pluralize(
      totalIncompleteRecommendations,
      'listing recommendation',
      'listing recommendations',
    )} to help attract qualified renters. `;
  };

  const renderBody = () => (
    <>
      <Box
        data-testid="ListingCompletenessScore-Body"
        css={{
          display: 'flex',
          alignItems: isFromDashboard ? 'flex-start' : 'center',
          flexDirection: isFromDashboard ? 'column' : 'row',
        }}
      >
        {(isScreenDesktop || isFromDashboard) && (
          <Box
            data-testid="LCS-RightPieChart"
            css={{
              alignSelf: isFromDashboard ? 'flex-start' : 'center',
              marginRight: { base: 0, md: '400' },
              marginBottom: isFromDashboard ? '200' : 0,
            }}
          >
            <ScorePieChart
              isRecsComplete={totalIncompleteRecommendations === 0}
              completeness={completenessScore}
              isLoading={isLoading}
              isMultifamily={isMultifamily}
            />
          </Box>
        )}

        <Box
          css={{
            flexGrow: 1,
            flexDirection: { base: 'column', xl: 'row' },
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginRight: { base: 0, xl: 'tight' },
              marginBottom: previewListingCTA ? { base: '200', xl: 0 } : undefined,
              gap: 'tight',
            }}
          >
            <Box data-testid="LCS-HeaderAndSubText">
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Heading
                  completenessScore={completenessScore}
                  showIncompleteMessage={showIncompleteMessage}
                  isLoading={isLoading}
                  isMultifamily={isMultifamily ?? false}
                />

                {Boolean(!isFromDashboard && isScreenMobile) && (
                  <ScorePieChart
                    customPieChartSize={30}
                    isRecsComplete={totalIncompleteRecommendations === 0}
                    completeness={completenessScore}
                    isMultifamily={isMultifamily}
                    isLoading={isLoading}
                  />
                )}
              </Box>

              <LoadingText
                asChild={true}
                textStyle={isFromDashboard ? 'body-sm' : 'body'}
                isLoading={isLoading}
                loadingWidth="20rem"
              >
                <Paragraph>
                  {getSubText()}

                  {Boolean(isFromDashboard || Boolean(!isFromDashboard && previewListingCTA)) && (
                    <Anchor asChild css={{ textStyle: isFromDashboard ? 'body-sm' : 'body' }}>
                      <button type="button" onClick={() => setDisplayScoreDetailsModal(true)}>
                        Learn more
                      </button>
                    </Anchor>
                  )}
                </Paragraph>
              </LoadingText>
            </Box>

            {Boolean(!isFromDashboard && isScreenTablet) && (
              <ScorePieChart
                customPieChartSize={39}
                isRecsComplete={totalIncompleteRecommendations === 0}
                completeness={completenessScore}
                isMultifamily={isMultifamily}
                isLoading={isLoading}
              />
            )}
          </Box>

          {!isFromDashboard && !previewListingCTA && (
            <Box css={{ alignSelf: { base: 'flex-start', xl: 'center' }, marginTop: { base: 'tightest', xl: 0 } }}>
              <TextButton
                onClick={() => setDisplayScoreDetailsModal(true)}
                css={{ width: 'max-content' }}
                disabled={isLoading}
              >
                Learn more
              </TextButton>
            </Box>
          )}

          {previewListingCTA && previewListingCTA}
        </Box>
      </Box>
      {displayScoreDetailsModal && (
        <ScoreDetailsModal
          onClose={() => setDisplayScoreDetailsModal(false)}
          isRecsComplete={totalIncompleteRecommendations === 0}
          completeness={completenessScore}
          isFeed={isFeed}
          isMultifamily={isMultifamily}
        />
      )}
    </>
  );

  if (isFromDashboard) {
    return renderBody();
  }

  return (
    <Card
      data-testid="ListingCompletenessScore"
      tone="neutral"
      elevated={isFromPremiumAnalyticsPage}
      css={isFromPremiumAnalyticsPage ? { marginBottom: '600' } : undefined}
    >
      {renderBody()}
    </Card>
  );
};

export default ListingCompletenessScore;
